import { useState, useEffect, useRef } from "react";
import { X, Sparkles, ChevronRight, ChevronLeft, Zap, Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

// Routes where the wizard is contextually useful (treatment pages).
// On every other route the pill stays hidden to avoid getting in the way.
const TREATMENT_ROUTES = [
  "/skin-boosters",
  "/combos",
  "/dermal-fillers",
  "/laser-and-skin",
  "/medical-microneedling",
  "/botox-ottawa",
  "/prp-ottawa",
  "/lip-filler-ottawa",
  "/dermal-filler-ottawa",
  "/microneedling-ottawa",
  "/sculptra-ottawa",
  "/treatments",
];

// User is considered "actively engaged" if they interact with the booking flow.
const isPathATreatmentPage = (path) =>
  TREATMENT_ROUTES.some((r) => path === r || path.startsWith(r + "/") || path.startsWith(r + "?"));

const DISMISS_KEY = "not_sure_wizard_dismissed_at";  // sessionStorage
const DELAY_MS = 2 * 60 * 1000;  // 2 minutes

/**
 * "Not Sure?" 3-Question Wizard.
 *
 * A friendly modal that asks 3 tiny questions (concern → budget →
 * frequency) and lands the client on ONE recommended treatment card
 * with a big "Book Now" button. Cuts analysis paralysis on a site
 * with 45 combo packages.
 *
 * Usage: import + <NotSureWizard /> — component renders its own
 * floating trigger button and manages the open/close state.
 */

// Concern → treatment map. Each concern has EN + FR label and a set of
// 3 candidates sorted by [budget tier][frequency] so we can pick the
// perfect match given all 3 answers.
const CONCERNS = [
  { key: "wrinkles",     en: "Fine lines & wrinkles",     fr: "Rides et ridules" },
  { key: "dark_circles", en: "Dark circles / tired eyes", fr: "Cernes / yeux fatigués" },
  { key: "thin_lips",    en: "Thin lips",                 fr: "Lèvres fines" },
  { key: "sagging",      en: "Sagging jawline / neck",    fr: "Mâchoire / cou relâchés" },
  { key: "dull_skin",    en: "Dull, tired-looking skin",  fr: "Peau terne, fatiguée" },
  { key: "acne_scars",   en: "Acne scars / texture",      fr: "Cicatrices / texture" },
  { key: "volume_loss",  en: "Lost volume in cheeks",     fr: "Perte de volume" },
  { key: "hair_loss",    en: "Hair loss / thinning",      fr: "Perte de cheveux" },
  { key: "hair_removal", en: "Unwanted body hair",        fr: "Poils indésirables" },
];

const BUDGETS = [
  { key: "under_500",  en: "Under $500",              fr: "Moins de 500 $" },
  { key: "500_1500",   en: "$500 – $1,500",           fr: "500 – 1 500 $" },
  { key: "1500_plus",  en: "$1,500+",                 fr: "1 500 $ et plus" },
  { key: "best_value", en: "Show me best value",      fr: "Meilleur rapport qualité-prix" },
];

const FREQUENCIES = [
  { key: "one_time",   en: "One-and-done",             fr: "Une seule fois" },
  { key: "few_yearly", en: "A few times a year",       fr: "Quelques fois par an" },
  { key: "member",     en: "Regular member / monthly", fr: "Membre régulier / mensuel" },
];

// Recommendation matrix — [concern][budget][frequency] → treatment
// We use a fallback chain: exact match > budget match > concern default.
const RECOMMENDATIONS = {
  wrinkles: {
    default: { name: "Full Upper Face Botox Package", price: "$599", route: "/botox-ottawa#pricing", why: "7 areas · UNLIMITED units · lasts 3-4 months — the classic wrinkle solution." },
    under_500: { name: "Forehead + 11's Botox", price: "$349", route: "/botox-ottawa#pricing", why: "The 2 most-requested areas with UNLIMITED units for under $500." },
    "500_1500": { name: "The #1 Choice Glow", price: "$1,199", route: "/combos#combos-botox-booster", why: "Full Face Botox + 3ml Redensity 1 — our most-picked upper-face transformation." },
    "1500_plus": { name: "Ultimate Skin Transformation (3-Month)", price: "$3,499", route: "/combos#combos-vip", why: "3 Botox + 3 Face/Neck Redensity sessions across 3 months with progress photos." },
    best_value: { name: "The Nefertiti Glow Combo", price: "$899", route: "/combos#combos-botox-booster", why: "Nefertiti Botox + 3ml Redensity 1 — biggest impact per dollar." },
    member: { name: "Signature Skin Membership", price: "$249/mo", route: "/skin-boosters?filter=programs", why: "Booster every 60-90 days + 10% off add-ons — perfect monthly plan." },
  },
  dark_circles: {
    default: { name: "Eye Revival", price: "$449", route: "/skin-boosters?filter=core", why: "Under-eye Redensity 1 — softens dark circles, fine lines & hollowness in one visit." },
    under_500: { name: "Eye Revival", price: "$449", route: "/skin-boosters?filter=core", why: "Under-eye Redensity 1 — softens dark circles, fine lines & hollowness in one visit." },
    "500_1500": { name: "The Eye Brightening Combo", price: "$699", route: "/combos#combos-botox-booster", why: "Crow's Feet Botox + Under-Eye Redensity 1 — brighter & plumper in one session." },
    "1500_plus": { name: "Eye Perfection Program (3 sessions)", price: "$1,199", route: "/skin-boosters?filter=programs", why: "3 under-eye Redensity 1 sessions — progressive dark-circle reduction. Save $148." },
    best_value: { name: "The Eye Brightening Combo", price: "$699", route: "/combos#combos-botox-booster", why: "Best 2-in-1 pick for dark circles + crow's feet." },
    member: { name: "Signature Skin Membership", price: "$249/mo", route: "/skin-boosters?filter=programs", why: "Includes an under-eye booster every 60-90 days on rotation." },
  },
  thin_lips: {
    default: { name: "1 Syringe Filler + FREE Lip Flip", price: "$499", route: "/lip-filler-ottawa#treatment-areas", why: "1ml filler + free Botox Lip Flip with UNLIMITED units. Karine's most-booked lip combo." },
    under_500: { name: "½ Syringe Filler + FREE Lip Flip", price: "$299", route: "/lip-filler-ottawa#treatment-areas", why: "Our door-crasher lip price — subtle enhancement + free Lip Flip Botox." },
    "500_1500": { name: "1 Syringe Filler + FREE Lip Flip", price: "$499", route: "/lip-filler-ottawa#treatment-areas", why: "Full 1ml with free Botox — most-picked package." },
    "1500_plus": { name: "2 Syringes Filler + FREE Botox", price: "$950", route: "/lip-filler-ottawa#treatment-areas", why: "2ml lip filler + choice of free Botox (Jelly Roll, Smile Lift or Lip Flip)." },
    best_value: { name: "½ Syringe Filler + FREE Lip Flip", price: "$299", route: "/lip-filler-ottawa#treatment-areas", why: "Highest impact for the smallest budget." },
    member: { name: "The Signature Skin Membership", price: "$249/mo", route: "/skin-boosters?filter=programs", why: "Add a lip top-up on your quarterly visit with 10% off." },
  },
  sagging: {
    default: { name: "Nefertiti Jaw & Neck Lift", price: "$479", route: "/combos#combos-botox", why: "Jawline + Neck Bands Botox with UNLIMITED units — elegant contour in one visit." },
    under_500: { name: "Nefertiti Jaw & Neck Lift", price: "$479", route: "/combos#combos-botox", why: "Under $500 for a full jawline sculpt with UNLIMITED Botox units." },
    "500_1500": { name: "The Radiance Lift (Face + Neck)", price: "$899", route: "/skin-boosters?filter=core", why: "Full face + neck Redensity 1 — save $299 vs. booking separately." },
    "1500_plus": { name: "The Platinum Rejuvenation", price: "$1,899", route: "/combos#combos-botox-booster", why: "All 8 Botox areas + 9ml Redensity 1 across face+neck+décolleté." },
    best_value: { name: "The Nefertiti Glow", price: "$899", route: "/combos#combos-botox-booster", why: "Nefertiti Botox + 3ml Redensity 1 — biggest lift-and-glow bang." },
    member: { name: "Crown Reserve Signature (Annual VIP)", price: "$4,999", route: "/combos#combos-vip", why: "4 quarterly jaw-Botox visits + 3 face/neck/décolleté sessions all year." },
  },
  dull_skin: {
    default: { name: "The Baby Glow Infusion", price: "$699", route: "/skin-boosters?filter=core", why: "Full-face Redensity 1 — Karine's #1 pick for glow. One session = same-week radiance." },
    under_500: { name: "HydraFacial + Laser Genesis Combo", price: "$349", route: "/combos#combos-laser", why: "Complete facial makeover under $350. Save $21 vs. separately." },
    "500_1500": { name: "The Baby Glow Infusion", price: "$699", route: "/skin-boosters?filter=core", why: "Full-face Redensity 1 — Karine's #1 pick for real glow transformation." },
    "1500_plus": { name: "The Glass Skin Program (3 sessions)", price: "$1,799", route: "/skin-boosters?filter=programs", why: "3 Redensity 1 sessions for cumulative glass-skin glow. Save $298." },
    best_value: { name: "Microneedling + 3ML Skin Booster", price: "$899", route: "/skin-boosters?filter=combos", why: "Best-value combo — microneedling + full Redensity 1 = collagen + glow together." },
    member: { name: "Signature Skin Membership", price: "$249/mo", route: "/skin-boosters?filter=programs", why: "Booster every 60-90 days keeps the glow year-round." },
  },
  acne_scars: {
    default: { name: "Microneedling 3-Treatment Package", price: "$1,099", route: "/medical-microneedling#pricing", why: "The recommended protocol for optimal collagen remodeling & scar improvement. Save $151." },
    under_500: { name: "Medical Microneedling (Single)", price: "$350", route: "/medical-microneedling#pricing", why: "Perfect first-timer intro to microneedling for scars & texture." },
    "500_1500": { name: "Microneedling 3-Treatment Package", price: "$1,099", route: "/medical-microneedling#pricing", why: "3 sessions of medical microneedling — dramatic scar improvement. Save $151." },
    "1500_plus": { name: "Vampire Facelift + 3 Microneedling Sessions", price: "$1,849", route: "/prp-ottawa#treatment-areas", why: "PRP + microneedling — natural collagen rebuild for scars." },
    best_value: { name: "Vampire Facelift (PRP + Microneedling)", price: "$750", route: "/prp-ottawa#treatment-areas", why: "PRP from your own blood + microneedling — natural scar remodeling." },
    member: { name: "Signature Skin Membership", price: "$249/mo", route: "/skin-boosters?filter=programs", why: "Adds a booster after each microneedling session for enhanced healing." },
  },
  volume_loss: {
    default: { name: "2 Syringes Filler + FREE Botox", price: "$950", route: "/dermal-fillers#pricing", why: "2ml filler for cheek contour + free Botox area. UNLIMITED units on free area." },
    under_500: { name: "1 Syringe Filler + FREE Lip Flip", price: "$499", route: "/dermal-fillers#pricing", why: "Perfect intro — 1ml volume restoration + free Botox Lip Flip." },
    "500_1500": { name: "2 Syringes Filler + FREE Botox", price: "$950", route: "/dermal-fillers#pricing", why: "2ml dermal filler + free Botox area with UNLIMITED units." },
    "1500_plus": { name: "Sculptra + Full Upper Face Botox", price: "$1,299", route: "/combos#combos-laser", why: "Sculptra collagen rebuild (2+ years) + full-face Botox smoothing." },
    best_value: { name: "3 Syringes Filler + FREE Botox", price: "$1,349", route: "/dermal-fillers#pricing", why: "3ml filler + free 11's OR Forehead Botox. Best per-ml value." },
    member: { name: "Crown Reserve Signature (Annual VIP)", price: "$4,999", route: "/combos#combos-vip", why: "Full year of volume maintenance across face+neck+décolleté." },
  },
  hair_loss: {
    default: { name: "PRP Hair Loss (4 Treatments)", price: "$2,499", route: "/prp-ottawa#treatment-areas", why: "Complete hair restoration program — stimulates dormant follicles + boosts blood supply." },
    under_500: { name: "PRP Single Treatment (Scalp)", price: "$699", route: "/prp-ottawa#treatment-areas", why: "First scalp PRP session — see how your follicles respond." },
    "500_1500": { name: "PRP 3-Treatment Package (Scalp)", price: "$1,899", route: "/prp-ottawa#treatment-areas", why: "3 scalp PRP sessions — the recommended entry protocol." },
    "1500_plus": { name: "PRP Hair Loss (4 Treatments)", price: "$2,499", route: "/prp-ottawa#treatment-areas", why: "Complete hair restoration program — 4 scalp PRP injections." },
    best_value: { name: "PRP 3-Treatment Package", price: "$1,899", route: "/prp-ottawa#treatment-areas", why: "Best value 3-pack for early hair-loss intervention." },
    member: { name: "PRP Hair Loss (4 Treatments)", price: "$2,499", route: "/prp-ottawa#treatment-areas", why: "Full program up front, then maintenance top-ups quarterly." },
  },
  hair_removal: {
    // Feb 2026 · Karine ask — Crown Packages retired. Everyone now
    // books by single area or stacks via "The House Package" (Buy any
    // 5 → the 6th is FREE at equal or lesser value) or the Head-to-Toe
    // Unlimited plan.
    default:    { name: "The House Package · Buy 5 → 6th FREE",       price: "Equal or lesser value", route: "/laser-hair-removal", why: "Our best per-area value — stack across any mix of areas, 6th is free automatically." },
    under_500:  { name: "Single Area · Priced by Zone",               price: "From $15",              route: "/laser-hair-removal", why: "Try one area (Linea, upper lip, underarms, etc.) as your intro." },
    "500_1500": { name: "The House Package · Buy 5 → 6th FREE",       price: "Equal or lesser value", route: "/laser-hair-removal", why: "Stack any 5 areas and the 6th comes free — ideal for maintenance." },
    "1500_plus":{ name: "Head-to-Toe Unlimited · 6-month plan",       price: "$249/mo × 6",           route: "/laser-hair-removal", why: "Unlimited head-to-toe sessions — every area, every month, for 6 months." },
    best_value: { name: "The House Package · Buy 5 → 6th FREE",       price: "Equal or lesser value", route: "/laser-hair-removal", why: "Best-value — mix any 5 and the 6th is on us (equal or lesser)." },
    member:     { name: "Head-to-Toe Unlimited · 6-month plan",       price: "$249/mo × 6",           route: "/laser-hair-removal", why: "Unlimited sessions roll with your monthly rhythm." },
  },
};

const getRecommendation = (concern, budget, frequency) => {
  const bucket = RECOMMENDATIONS[concern] || RECOMMENDATIONS.wrinkles;
  // Priority: exact freq (member overrides budget) → budget → default
  if (frequency === "member" && bucket.member) return bucket.member;
  if (bucket[budget]) return bucket[budget];
  return bucket.default;
};

const NotSureWizard = () => {
  const { language } = useLanguage();
  const isFr = language === "fr";
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ concern: null, budget: null, frequency: null });
  const [pillVisible, setPillVisible] = useState(false);
  const [currentPath, setCurrentPath] = useState(() =>
    typeof window !== "undefined" ? window.location.pathname : "/"
  );
  const timerRef = useRef(null);

  // Watch pathname via popstate + a light polling fallback for programmatic
  // navigation (react-router uses history.pushState which doesn't fire
  // popstate). Cheap: 700 ms tick and we only diff a string.
  useEffect(() => {
    const check = () => {
      const p = window.location.pathname;
      setCurrentPath((prev) => (prev === p ? prev : p));
    };
    const iv = setInterval(check, 700);
    window.addEventListener("popstate", check);
    return () => {
      clearInterval(iv);
      window.removeEventListener("popstate", check);
    };
  }, []);

  // Show the floating pill ONLY on treatment pages, and only AFTER a 2-min
  // dwell without a book action. Dismissing it hides it for the whole
  // browser session (sessionStorage). Route changes reset the timer.
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setPillVisible(false);

    try {
      if (sessionStorage.getItem(DISMISS_KEY)) return;
    } catch { /* private mode */ }

    if (!isPathATreatmentPage(currentPath)) return;

    timerRef.current = setTimeout(() => {
      setPillVisible(true);
    }, DELAY_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentPath]);

  const dismissPill = (e) => {
    e?.stopPropagation();
    setPillVisible(false);
    try {
      sessionStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch { /* private mode */ }
  };

  const reset = () => {
    setStep(0);
    setAnswers({ concern: null, budget: null, frequency: null });
  };
  const close = () => { setIsOpen(false); setTimeout(reset, 300); };

  const rec = (answers.concern && answers.budget && answers.frequency)
    ? getRecommendation(answers.concern, answers.budget, answers.frequency)
    : null;

  const questions = [
    {
      key: "concern",
      title: isFr ? "Quelle est votre principale préoccupation ?" : "What's your main concern?",
      subtitle: isFr ? "Choisissez celle qui vous ressemble le plus." : "Pick the one that sounds most like you.",
      options: CONCERNS,
    },
    {
      key: "budget",
      title: isFr ? "Quel est votre budget pour cette visite ?" : "What's your budget for this visit?",
      subtitle: isFr ? "Pas de jugement — juste une bonne recommandation." : "No judgment — just a better recommendation.",
      options: BUDGETS,
    },
    {
      key: "frequency",
      title: isFr ? "À quelle fréquence souhaitez-vous venir ?" : "How often do you want to visit?",
      subtitle: isFr ? "Cela nous aide à choisir un traitement ponctuel ou un programme." : "Helps us pick a one-time treat or a full program.",
      options: FREQUENCIES,
    },
  ];

  const current = questions[step];
  const totalSteps = questions.length;

  return (
    <>
      {/* Floating trigger — only after 2 min on treatment pages,
          dismissible for the session */}
      {pillVisible && (
        <div className="fixed bottom-24 right-4 z-40 flex items-center gap-1">
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            className="group inline-flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-xl hover:shadow-2xl transition-all animate-fade-in"
            data-testid="not-sure-wizard-trigger"
            aria-label={isFr ? "Ouvrir le questionnaire" : "Open recommendation wizard"}
          >
            <Zap className="w-4 h-4 group-hover:animate-pulse" strokeWidth={2.5} />
            <span className="text-sm font-semibold">
              {isFr ? "Pas sûr ? Trouvez votre soin" : "Not sure? Find your treatment"}
            </span>
          </button>
          <button
            type="button"
            onClick={dismissPill}
            className="w-8 h-8 rounded-full bg-white/95 backdrop-blur-md border border-amber-200 shadow-md hover:bg-zinc-100 hover:border-zinc-400 flex items-center justify-center transition-all animate-fade-in"
            data-testid="not-sure-wizard-dismiss-pill"
            aria-label={isFr ? "Non merci" : "No thanks"}
            title={isFr ? "Non merci — masquer" : "No thanks — hide"}
          >
            <X className="w-3.5 h-3.5 text-zinc-600" strokeWidth={2.5} />
          </button>
        </div>
      )}

      {isOpen && (
        <div
          className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={close}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-fade-in"
            onClick={(e) => e.stopPropagation()}
            data-testid="not-sure-wizard-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-amber-50 via-white to-amber-50 border-b border-amber-100">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-600" />
                <div>
                  <h3 className="text-base font-semibold text-zinc-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {rec ? (isFr ? "Votre recommandation personnalisée" : "Your personal recommendation") : (isFr ? "Trouvez votre traitement idéal" : "Find your ideal treatment")}
                  </h3>
                  <p className="text-[11px] text-amber-700 mt-0.5">
                    {rec ? (isFr ? "Vous êtes prête à réserver" : "You're ready to book") : `${isFr ? "Question" : "Question"} ${step + 1} / ${totalSteps}`}
                  </p>
                </div>
              </div>
              <button onClick={close} className="p-2 rounded-full hover:bg-zinc-100 transition-colors" data-testid="not-sure-wizard-close">
                <X className="w-4 h-4 text-zinc-500" />
              </button>
            </div>

            {/* Progress bar */}
            {!rec && (
              <div className="h-1 bg-amber-100">
                <div className="h-full bg-amber-500 transition-all" style={{ width: `${((step + 1) / totalSteps) * 100}%` }} />
              </div>
            )}

            {/* Recommendation view */}
            {rec ? (
              <div className="p-6" data-testid="not-sure-wizard-result">
                <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-400 rounded-2xl p-5 mb-4 shadow-sm">
                  <p className="text-[10px] uppercase tracking-[2px] font-bold text-amber-700 mb-2 flex items-center gap-1.5">
                    <Zap className="w-3 h-3" />
                    {isFr ? "RECOMMANDÉ POUR VOUS" : "RECOMMENDED FOR YOU"}
                  </p>
                  <h4 className="text-2xl font-semibold text-zinc-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {rec.name}
                  </h4>
                  <p className="text-2xl font-bold text-amber-700 mb-3">{rec.price}</p>
                  <p className="text-sm text-zinc-700 leading-relaxed mb-4">{rec.why}</p>
                  <button
                    type="button"
                    onClick={() => {
                      close();
                      // Navigate + auto-open booking modal via ?book=1
                      const url = `${rec.route}${rec.route.includes('?') ? '&' : '?'}book=1`;
                      window.location.href = url;
                    }}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-md hover:shadow-lg transition-all"
                    data-testid="not-sure-wizard-book"
                  >
                    <Check className="w-4 h-4" strokeWidth={3} />
                    {isFr ? "Voir & Réserver" : "See it & Book"}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={reset}
                  className="w-full text-center text-sm text-zinc-500 hover:text-amber-700 transition-colors"
                  data-testid="not-sure-wizard-retry"
                >
                  {isFr ? "← Recommencer" : "← Start over"}
                </button>
              </div>
            ) : (
              /* Question view */
              <div className="p-6">
                <h4 className="text-lg font-semibold text-zinc-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {current.title}
                </h4>
                <p className="text-xs text-zinc-500 mb-4">{current.subtitle}</p>
                <div className="space-y-2 max-h-[340px] overflow-y-auto">
                  {current.options.map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => {
                        const next = { ...answers, [current.key]: opt.key };
                        setAnswers(next);
                        // Auto-advance
                        if (step < totalSteps - 1) setStep(step + 1);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${
                        answers[current.key] === opt.key
                          ? "border-amber-500 bg-amber-50 text-amber-900"
                          : "border-zinc-200 hover:border-amber-300 hover:bg-amber-50/40 text-zinc-800"
                      }`}
                      data-testid={`not-sure-option-${current.key}-${opt.key}`}
                    >
                      <span className="font-medium">{isFr ? opt.fr : opt.en}</span>
                    </button>
                  ))}
                </div>
                {step > 0 && (
                  <button
                    onClick={() => setStep(step - 1)}
                    className="mt-4 inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-amber-700 transition-colors"
                    data-testid="not-sure-wizard-back"
                  >
                    <ChevronLeft className="w-3 h-3" />
                    {isFr ? "Retour" : "Back"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default NotSureWizard;
